import {
  type Column,
  type ColumnDef,
  isPricingModeRelative,
  OrderFormSides,
  type PriceParams,
  type QuoteStatusColumnParams,
  SideEnum,
  type SizeColumnParams,
  type SpreadColumnParams,
} from '@talos/kyoko';
import { compact } from 'lodash-es';

import { useMemo } from 'react';
import { CompositeQuoteRow, LegQuoteRow, type QuoteBreakdownRow } from './rows';

interface UseQuoteBreakdownColumnsParams {
  shownSide: OrderFormSides;
  sizeCounteringEnabled: boolean;
}

const sizeParams = {
  currencyField: 'Currency',
} satisfies SizeColumnParams<QuoteBreakdownRow>;

export const useQuoteBreakdownColumns = ({ shownSide, sizeCounteringEnabled }: UseQuoteBreakdownColumnsParams) => {
  const columns: Column[] = useMemo(() => {
    return compact([
      {
        type: 'marketAccount',
        field: 'MarketAccount',
        headerValueGetter: () => 'Dealer',
        hide: true,
      },
      {
        type: 'security',
        field: 'Symbol',
        width: 150,
      },
      {
        type: 'quoteStatus',
        field: 'quoteMarket.QuoteStatus',
        title: 'Status',
        width: 110,
        params: {
          tooltipField: 'quoteMarket.Text', // reject reason
        } satisfies QuoteStatusColumnParams<QuoteBreakdownRow>,
      },
      {
        type: 'size',
        field: 'Quantity',
        title: 'Quote Qty',
        params: sizeParams,
      },
      sizeCounteringEnabled &&
        (shownSide === OrderFormSides.Twoway || shownSide === OrderFormSides.Sell) && {
          type: 'size',
          field: 'BidQty',
          title: 'Bid Qty',
          params: sizeParams,
        },
      (shownSide === OrderFormSides.Twoway || shownSide === OrderFormSides.Sell) && {
        type: 'price',
        title: 'Sell',
        field: 'BidPx',
        params: {
          quoteCurrencyField: 'AmountCurrency',
          getSide: data => (data instanceof LegQuoteRow ? SideEnum.Sell : undefined),
          isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          getIsBestPrice: data => (data instanceof CompositeQuoteRow ? data.quoteMarket.isBestBid : false),
        } satisfies PriceParams<QuoteBreakdownRow>,
      },
      shownSide === OrderFormSides.Twoway && {
        id: 'spread',
        type: 'spread',
        title: 'Spread',
        params: {
          bidField: 'BidPx',
          askField: 'OfferPx',
          pricingModeField: 'PricingMode',
        } satisfies SpreadColumnParams<QuoteBreakdownRow>,
      },
      (shownSide === OrderFormSides.Twoway || shownSide === OrderFormSides.Buy) && {
        type: 'price',
        title: 'Buy',
        field: 'OfferPx',
        params: {
          align: 'left',
          quoteCurrencyField: 'AmountCurrency',
          getSide: data => (data instanceof LegQuoteRow ? SideEnum.Buy : undefined),
          isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          getIsBestPrice: data => (data instanceof CompositeQuoteRow ? data.quoteMarket.isBestOffer : false),
        } satisfies PriceParams<QuoteBreakdownRow>,
      },
      sizeCounteringEnabled &&
        (shownSide === OrderFormSides.Twoway || shownSide === OrderFormSides.Buy) && {
          type: 'size',
          field: 'OfferQty',
          title: 'Offer Qty',
          params: sizeParams,
        },
    ] satisfies (ColumnDef<QuoteBreakdownRow> | false)[]);
  }, [shownSide, sizeCounteringEnabled]);

  return columns;
};
